import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  OfficeAgendaItemResponse,
  useGetOfficesAgendasQuery,
} from '@/hooks/queries/use-get-offices-agendas';

export const SelectOffice = ({
  clearable = true,
  defaultId,
  placeholder = 'select-office.placeholder',
  width = 'w-48',
  doctorSubstituteId,
  onValueChange = () => {},
}: {
  placeholder?: string;
  width?: string;
  clearable?: boolean;
  defaultId?: string;
  doctorSubstituteId?: string;
  onValueChange: (officeId: OfficeAgendaItemResponse | undefined) => void;
}) => {
  const { t } = useTranslation();

  const { data: officesAgendasResponse, isPending } = useGetOfficesAgendasQuery(
    {
      doctorSubstituteId,
    },
  );

  const [selectedOfficeId, setSelectedOfficeId] = useState<string | 'none'>(
    'none',
  );

  // Set default value
  useEffect(() => {
    if (selectedOfficeId !== 'none') {
      return;
    }

    if (
      (defaultId || !clearable) &&
      officesAgendasResponse?.data &&
      officesAgendasResponse.data.length > 0
    ) {
      let office: OfficeAgendaItemResponse | undefined;
      if (defaultId) {
        office = officesAgendasResponse.data.find((i) => i.id === defaultId);
      } else {
        const [first] = officesAgendasResponse.data;
        office = first;
      }

      if (office) {
        setSelectedOfficeId(office.id);
      }
    }
  }, [officesAgendasResponse, isPending, defaultId]);

  // Emit change value + set placeholder name
  useEffect(() => {
    if (!officesAgendasResponse?.data) {
      return;
    }

    let office: OfficeAgendaItemResponse | undefined;
    if (!selectedOfficeId || selectedOfficeId === 'none') {
      office = undefined;
    } else {
      office = officesAgendasResponse.data.find(
        (o) => o.id === selectedOfficeId,
      );
    }

    onValueChange(office);
  }, [selectedOfficeId, officesAgendasResponse, isPending]);

  const officesOptions: {
    id: string;
    value: string;
    label: string;
  }[] = useMemo(() => {
    const _officesOptions = (officesAgendasResponse?.data ?? []).map(
      (office) => {
        return {
          id: office.id,
          value: office.id,
          label: office.name,
        };
      },
    );
    if (clearable) {
      return [
        {
          id: 'none',
          value: 'none',
          label: t(placeholder),
        },
        ..._officesOptions,
      ];
    } else {
      return _officesOptions;
    }
  }, [clearable, officesAgendasResponse, isPending, t]);

  return (
    <Select
      value={selectedOfficeId}
      onValueChange={(value) => {
        setSelectedOfficeId(value);
      }}
    >
      <SelectTrigger className={width}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent side="top">
        {officesOptions.map((option) => (
          <SelectItem
            key={option.id}
            value={option.value}
          >
            {option.id === 'none' ? (
              <span className="text-muted-foreground">{option.label}</span>
            ) : (
              <span>{option.label}</span>
            )}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};
